import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  stats: {
    totalAssessments: 0,
    pending: 0,
    completed: 0,
    averageScore: 0,
    upcomingDeadlines: 0,
  },
  upcomingAssessments: [],
  recentResults: [],
  trends: [],
  currentClass: null,
  loading: false,
  error: null,
  lastFetched: null,
};

const studentDashboardSlice = createSlice({
  name: 'studentDashboard',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Set dashboard data
    setDashboardData: (state, action) => {
      const { stats, upcomingAssessments, recentResults, currentClass, unreadNotifications } = action.payload;
      state.stats = stats || state.stats;
      state.upcomingAssessments = upcomingAssessments || [];
      state.recentResults = recentResults || [];
      state.currentClass = currentClass || null;
      state.unreadNotifications = unreadNotifications || 0;
      state.loading = false;
      state.error = null;
      state.lastFetched = Date.now();
    },

    // Set stats only
    setStats: (state, action) => {
      state.stats = action.payload;
      state.loading = false;
    },

    // Set upcoming assessments
    setUpcoming: (state, action) => {
      state.upcomingAssessments = action.payload;
    },

    // Set recent results
    setRecent: (state, action) => {
      state.recentResults = action.payload;
    },

    // Set trends
    setTrends: (state, action) => {
      state.trends = action.payload;
    },

    // Set current class
    setCurrentClass: (state, action) => {
      state.currentClass = action.payload;
    },

    // Reset dashboard
    resetDashboard: () => initialState,

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

// Export actions
export const {
  setLoading,
  setError,
  setDashboardData,
  setStats,
  setUpcoming,
  setRecent,
  setTrends,
  setCurrentClass,
  resetDashboard,
  clearError,
} = studentDashboardSlice.actions;

// Selectors
export const selectDashboardStats = (state) => state.studentDashboard.stats;
export const selectUpcomingAssessments = (state) => state.studentDashboard.upcomingAssessments;
export const selectRecentResults = (state) => state.studentDashboard.recentResults;
export const selectTrends = (state) => state.studentDashboard.trends;
export const selectCurrentClass = (state) => state.studentDashboard.currentClass;
export const selectDashboardLoading = (state) => state.studentDashboard.loading;
export const selectDashboardError = (state) => state.studentDashboard.error;
export const selectLastFetched = (state) => state.studentDashboard.lastFetched;

// Helper to check if cache is valid (5 minutes TTL)
export const isCacheValid = (state) => {
  const lastFetched = state.studentDashboard.lastFetched;
  if (!lastFetched) return false;
  return Date.now() - lastFetched < 5 * 60 * 1000;
};

export default studentDashboardSlice.reducer;
